import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  GitPullRequest, Check, X, Play, Clock, AlertTriangle,
  Shield, GitBranch, RefreshCw, ChevronDown, History,
  FileText, Globe, Package,
  CheckCircle, XCircle, Loader2, Settings,
  GitCommit, Network, Trash2,
} from "lucide-react";
import { authFetch } from "../utils/authFetch";
import { useProject } from "../contexts/ProjectContext";
import { useProjectEvents } from "../hooks/useProjectEvents";
import CommitGraphVisualizer from "../components/docs/CommitGraphVisualizer";
import CommitDiffPanel from "../components/docs/CommitDiffPanel";
import MergeRequestDashboardPage from "./MergeRequestDashboardPage";
import BranchDiffPage from "./BranchDiffPage";

interface Branch {
  id: string;
  name: string;
  slug: string;
  isProtected: boolean;
  sequence: number;
  headCommitId?: string | null;
  createdAt: string | null;
}

interface RebaseRequest {
  id: string;
  branchId: string;
  targetBranchId: string;
  status: "open" | "approved" | "executed" | "cancelled" | "needs_resolution";
  createdBy: string;
  approvedBy: string | null;
  approvedAt: string | null;
  executedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Commit {
  id: string;
  sequence: number;
  parentId?: string | null;
  message?: string | null;
  authorId?: string | null;
  authorName?: string | null;
  changes?: { id?: string; action: string; filePath: string; contentHash?: string | null }[];
  createdAt?: string | null;
}

interface Build {
  id: string;
  buildNumber: number;
  contentHash: string;
  status: "building" | "completed" | "failed";
  createdAt: string | null;
  builtBy: string | null;
  builtByName: string | null;
  pagesStored: number;
  pagesDeduped: number;
}

const STATUS_CONFIG: Record<string, { bg: string; text: string; border: string; icon: typeof Clock; label: string }> = {
  open: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", icon: Clock, label: "Open" },
  approved: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", icon: Check, label: "Approved" },
  executed: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", icon: Check, label: "Executed" },
  cancelled: { bg: "bg-slate-50", text: "text-slate-500", border: "border-slate-200", icon: X, label: "Cancelled" },
  needs_resolution: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", icon: AlertTriangle, label: "Needs Resolution" },
};


const BUILD_STATUS: Record<string, { icon: typeof CheckCircle; color: string; bg: string; label: string }> = {
  completed: { icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200", label: "Completed" },
  building: { icon: Loader2, color: "text-blue-600", bg: "bg-blue-50 border-blue-200", label: "Building" },
  failed: { icon: XCircle, color: "text-red-600", bg: "bg-red-50 border-red-200", label: "Failed" },
};

type Tab = "branches" | "requests" | "commits" | "graph" | "builds" | "merge-requests" | "compare" | "settings";
type FilterStatus = "active" | "all" | "open" | "approved" | "executed" | "cancelled" | "needs_resolution";

export default function CmsAdminPage({ defaultTab }: { defaultTab?: Tab } = {}) {
  const { currentProject, isAdmin } = useProject();
  const [tab, setTab] = useState<Tab>(defaultTab || "branches");
  const [branches, setBranches] = useState<Branch[]>([]);
  const [requests, setRequests] = useState<RebaseRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<FilterStatus>("active");
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [commitBranchId, setCommitBranchId] = useState<string>("");
  const [commits, setCommits] = useState<Commit[]>([]);
  const [commitLoading, setCommitLoading] = useState(false);
  const [commitTotal, setCommitTotal] = useState(0);
  const [commitsLoadingMore, setCommitsLoadingMore] = useState(false);
  const commitReq = useRef(0);
  const [isPublished, setIsPublished] = useState(false);
  const [publishToggling, setPublishToggling] = useState(false);
  const [builds, setBuilds] = useState<Build[]>([]);
  const [buildTotal, setBuildTotal] = useState(0);
  const [buildLoading, setBuildLoading] = useState(false);
  const [buildLimit, setBuildLimit] = useState(20);
  const buildReq = useRef(0);

  const projectSlug = currentProject?.slug;
  const projectId = currentProject?.id || "";

  useEffect(() => {
    if (currentProject) setIsPublished(!!currentProject.isPublished);
  }, [currentProject]);

  const togglePublished = async () => {
    if (!currentProject || !isAdmin) return;
    setPublishToggling(true);
    const res = await authFetch(`/api/admin/projects/${currentProject.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ is_published: !isPublished }),
    });
    if (res.ok) setIsPublished(!isPublished);
    setPublishToggling(false);
  };

  const fetchBranches = useCallback(async () => {
    if (!projectSlug) return;
    const res = await authFetch(`/api/projects/${projectSlug}/studio/branches`);
    if (res.ok) setBranches(await res.json());
  }, [projectSlug]);

  const fetchRequests = useCallback(async () => {
    if (!projectSlug) return;
    setLoading(true);
    const res = await authFetch(`/api/projects/${projectSlug}/rebase-requests`);
    if (res.ok) setRequests(await res.json());
    setLoading(false);
  }, [projectSlug]);

  useEffect(() => { fetchBranches(); fetchRequests(); }, [fetchBranches, fetchRequests]);

  // offset > 0 appends the next page. Latest request wins: the commit walk is
  // slow on long branches, so a previously picked branch's response can land
  // after the current one's and would show its commits under the selected branch.
  const fetchCommits = useCallback(async (branchId: string, offset = 0) => {
    if (!projectSlug || !branchId) return;
    const seq = offset ? commitReq.current : ++commitReq.current;
    if (offset) setCommitsLoadingMore(true); else setCommitLoading(true);
    const res = await authFetch(`/api/projects/${projectSlug}/studio/branches/${branchId}/commits?limit=50&offset=${offset}`).catch(() => null);
    const data = res?.ok ? await res.json().catch(() => null) : null;
    setCommitsLoadingMore(false);
    if (seq !== commitReq.current) return;
    setCommitLoading(false);
    if (offset && !data) return;
    // A failed first page clears the list rather than leaving the previous branch's commits.
    const list: Commit[] = data?.commits || [];
    setCommits(prev => offset ? [...prev, ...list.filter(c => !prev.some(p => p.id === c.id))] : list);
    setCommitTotal(data?.total ?? list.length);
  }, [projectSlug]);

  // "Show more" raises the limit rather than appending by offset: build_started
  // events add rows at the top (shifting offsets), and SSE/Refresh refetch from
  // the top, so a re-fetch at the larger limit keeps everything already shown.
  const fetchBuilds = useCallback(async () => {
    if (!projectSlug) return;
    const seq = ++buildReq.current;
    setBuildLoading(true);
    const res = await authFetch(`/api/docs/${projectSlug}/history?limit=${buildLimit}`).catch(() => null);
    const data = res?.ok ? await res.json().catch(() => null) : null;
    if (seq !== buildReq.current) return;
    if (data) {
      setBuilds(data.builds);
      setBuildTotal(data.total);
    }
    setBuildLoading(false);
  }, [projectSlug, buildLimit]);

  useEffect(() => {
    if (commitBranchId) fetchCommits(commitBranchId);
    else { setCommits([]); }
  }, [commitBranchId, fetchCommits]);

  // Auto-select live branch for commits when branches load
  useEffect(() => {
    if (branches.length > 0 && !commitBranchId) {
      const live = branches.find(b => b.name === "main");
      if (live) setCommitBranchId(live.id);
    }
  }, [branches]);

  // Fetch builds when tab switches to builds
  useEffect(() => {
    if (tab === "builds") fetchBuilds();
  }, [tab, fetchBuilds]);

  // SSE: listen for build events and refresh builds list
  useProjectEvents(projectSlug, useCallback((event) => {
    if (event.type === "build_completed" || event.type === "build_started" || event.type === "build_failed") {
      fetchBuilds();
    }
  }, [fetchBuilds]));

  const branchName = (id: string) => branches.find(b => b.id === id)?.name || id.slice(0, 8);

  const filteredRequests = requests.filter(r => {
    if (filter === "active") return ["open", "approved", "needs_resolution"].includes(r.status);
    if (filter === "all") return true;
    return r.status === filter;
  });

  const approve = async (id: string) => {
    setActionLoading(id);
    await authFetch(`/api/projects/${projectSlug}/rebase-requests/${id}/approve`, { method: "POST" });
    await fetchRequests();
    setActionLoading(null);
  };

  const execute = async (id: string) => {
    setActionLoading(id);
    const res = await authFetch(`/api/projects/${projectSlug}/rebase-requests/${id}/execute`, { method: "POST" });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      alert(err.detail || "Execution failed");
    }
    await fetchRequests();
    await fetchBranches();
    setActionLoading(null);
  };

  const cancel = async (id: string) => {
    setActionLoading(id);
    await authFetch(`/api/projects/${projectSlug}/rebase-requests/${id}/cancel`, { method: "POST" });
    await fetchRequests();
    setActionLoading(null);
  };

  const toggleProtect = async (branchId: string, isProtected: boolean) => {
    const res = await authFetch(`/api/projects/${projectSlug}/studio/branches/${branchId}/protect`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ isProtected }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      alert(err.detail || "Failed to update branch protection");
      return;
    }
    await fetchBranches();
  };

  const deleteBranch = async (branchId: string) => {
    const res = await authFetch(`/api/projects/${projectSlug}/studio/branches/${branchId}`, { method: "DELETE" });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      alert(err.detail || "Failed to delete branch");
      return;
    }
    await fetchBranches();
  };

  const activeCount = requests.filter(r => ["open", "approved", "needs_resolution"].includes(r.status)).length;

  const TABS: { key: Tab; label: string; icon: typeof GitBranch; badge?: number }[] = [
    { key: "branches", label: "Branches", icon: GitBranch },
    { key: "requests", label: "Requests", icon: GitPullRequest, badge: activeCount || undefined },
    { key: "commits", label: "Commits", icon: History },
    { key: "graph", label: "Graph", icon: Network },
    { key: "builds", label: "Builds", icon: Package, badge: buildTotal || undefined },
    { key: "merge-requests", label: "Requests", icon: GitPullRequest },
    { key: "compare", label: "Compare", icon: GitBranch },
    { key: "settings", label: "Settings", icon: Settings },
  ];

  // When used from SettingsLayout with a specific tab, skip header/tabs
  if (defaultTab) {
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {tab === "branches" && <BranchesTab branches={branches} isAdmin={isAdmin} toggleProtect={toggleProtect} deleteBranch={deleteBranch} />}
          {tab === "requests" && (
            <RequestsTab
              requests={filteredRequests} filter={filter} setFilter={setFilter}
              loading={loading} actionLoading={actionLoading}
              expandedId={expandedId} setExpandedId={setExpandedId}
              branchName={branchName} isAdmin={isAdmin}
              approve={approve} execute={execute} cancel={cancel}
            />
          )}
          {tab === "commits" && (
            <CommitsTab
              projectSlug={projectSlug || ""} branches={branches}
              commitBranchId={commitBranchId} setCommitBranchId={setCommitBranchId}
              commits={commits} commitLoading={commitLoading}
              commitTotal={commitTotal} loadingMore={commitsLoadingMore}
              onLoadMore={() => fetchCommits(commitBranchId, commits.length)}
              onRefresh={() => { if (commitBranchId) fetchCommits(commitBranchId); }}
            />
          )}
          {tab === "graph" && projectSlug && (
            <div style={{ height: 500 }}><CommitGraphVisualizer projectSlug={projectSlug} /></div>
          )}
          {tab === "builds" && (
            <BuildsTab builds={builds} buildTotal={buildTotal} buildLoading={buildLoading} onRefresh={fetchBuilds} onLoadMore={() => setBuildLimit(l => l + 20)} projectId={projectId} isAdmin={isAdmin} />
          )}
          {tab === "merge-requests" && <MergeRequestDashboardPage />}
          {tab === "compare" && <BranchDiffPage />}
          {tab === "settings" && (
            <SettingsTab isPublished={isPublished} isAdmin={isAdmin}
              publishToggling={publishToggling} togglePublished={togglePublished} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">CMS Administration</h2>
          <p className="text-[13px] text-slate-500 mt-0.5">Manage branches, builds, and publishing</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { fetchBranches(); fetchRequests(); if (tab === "builds") fetchBuilds(); }}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            title="Refresh"
          >
            <RefreshCw className="size-4" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-3.5 py-2 text-[13px] font-medium border-b-2 transition-colors -mb-px ${
                tab === t.key
                  ? "border-indigo-500 text-indigo-700"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
              }`}
            >
              <Icon className="size-3.5" />
              {t.label}
              {t.badge != null && (
                <span className="px-1.5 py-px rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-semibold">{t.badge}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {tab === "branches" && <BranchesTab branches={branches} isAdmin={isAdmin} toggleProtect={toggleProtect} deleteBranch={deleteBranch} />}
        {tab === "requests" && (
          <RequestsTab
            requests={filteredRequests}
            filter={filter}
            setFilter={setFilter}
            loading={loading}
            actionLoading={actionLoading}
            expandedId={expandedId}
            setExpandedId={setExpandedId}
            branchName={branchName}
            isAdmin={isAdmin}
            approve={approve}
            execute={execute}
            cancel={cancel}
          />
        )}
        {tab === "commits" && (
          <CommitsTab
            projectSlug={projectSlug || ""}
            branches={branches}
            commitBranchId={commitBranchId}
            setCommitBranchId={setCommitBranchId}
            commits={commits}
            commitLoading={commitLoading}
            commitTotal={commitTotal}
            loadingMore={commitsLoadingMore}
            onLoadMore={() => fetchCommits(commitBranchId, commits.length)}
            onRefresh={() => { if (commitBranchId) fetchCommits(commitBranchId); }}
          />
        )}
        {tab === "graph" && projectSlug && (
          <div style={{ height: 500 }}>
            <CommitGraphVisualizer projectSlug={projectSlug} />
          </div>
        )}
        {tab === "builds" && (
          <BuildsTab builds={builds} buildTotal={buildTotal} buildLoading={buildLoading} onRefresh={fetchBuilds} onLoadMore={() => setBuildLimit(l => l + 20)} projectId={projectId} isAdmin={isAdmin} />
        )}
        {tab === "merge-requests" && <MergeRequestDashboardPage />}
        {tab === "compare" && <BranchDiffPage />}
        {tab === "settings" && (
          <SettingsTab
            isPublished={isPublished}
            isAdmin={isAdmin}
            publishToggling={publishToggling}
            togglePublished={togglePublished}
          />
        )}
      </div>
    </div>
  );
}

/* ── Branches Tab ─────────────────────────────────────────────────────── */
function BranchesTab({ branches, isAdmin, toggleProtect, deleteBranch }: {
  branches: Branch[];
  isAdmin: boolean;
  toggleProtect: (id: string, v: boolean) => void;
  deleteBranch: (id: string) => void;
}) {
  const [deleteTarget, setDeleteTarget] = useState<Branch | null>(null);
  const [deleting, setDeleting] = useState(false);

  if (branches.length === 0) {
    return <div className="px-5 py-12 text-center text-[13px] text-slate-400">No branches found for this version</div>;
  }
  return (
    <>
      <div className="divide-y divide-slate-100">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          <Shield className="size-3.5 text-indigo-500" />
          <span className="text-[12px] text-slate-500">Protected branches require rebase requests for merges</span>
        </div>
        {branches.map(b => {
          const isMain = b.name === "main";
          return (
            <div key={b.id} className="flex items-center justify-between px-5 py-3">
              <div className="flex items-center gap-2.5">
                <GitBranch className="size-4 text-slate-400" />
                <span className="text-[13px] font-medium text-slate-700">{b.name}</span>
                {isMain && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-600 border border-emerald-200/60">live</span>
                )}
              </div>
              <div className="flex items-center gap-3">
                {isAdmin && !isMain && (
                  <button onClick={() => setDeleteTarget(b)}
                    className="p-1 rounded-md text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                    title="Delete branch">
                    <Trash2 className="size-3.5" />
                  </button>
                )}
                {isAdmin && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <span className="text-[11px] text-slate-500">{b.isProtected ? "Protected" : "Unprotected"}</span>
                    <button
                      onClick={() => toggleProtect(b.id, !b.isProtected)}
                      className={`relative w-9 h-5 rounded-full transition-colors ${b.isProtected ? "bg-indigo-500" : "bg-slate-200"}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${b.isProtected ? "translate-x-4" : ""}`} />
                    </button>
                  </label>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="bg-white rounded-xl shadow-xl w-[380px] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
              <span className="text-sm font-semibold text-slate-800">Delete branch</span>
              <button onClick={() => setDeleteTarget(null)} disabled={deleting} className="p-1 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600">
                <X className="size-4" />
              </button>
            </div>
            <div className="px-5 py-4">
              <p className="text-sm text-slate-500 leading-relaxed">
                Are you sure you want to delete <span className="font-medium text-slate-700">{deleteTarget.name}</span>? All commits on this branch will be lost.
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200">
              <button onClick={() => setDeleteTarget(null)} disabled={deleting}
                className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">
                Cancel
              </button>
              <button onClick={async () => { setDeleting(true); await deleteBranch(deleteTarget.id); setDeleting(false); setDeleteTarget(null); }}
                disabled={deleting}
                className="px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-40 transition-colors">
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ── Requests Tab ─────────────────────────────────────────────────────── */
function RequestsTab({ requests, filter, setFilter, loading, actionLoading, expandedId, setExpandedId, branchName, isAdmin, approve, execute, cancel }: {
  requests: RebaseRequest[];
  filter: FilterStatus;
  setFilter: (f: FilterStatus) => void;
  loading: boolean;
  actionLoading: string | null;
  expandedId: string | null;
  setExpandedId: (id: string | null) => void;
  branchName: (id: string) => string;
  isAdmin: boolean;
  approve: (id: string) => void;
  execute: (id: string) => void;
  cancel: (id: string) => void;
}) {
  return (
    <>
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-1">
        {(["active", "all", "open", "approved", "executed", "needs_resolution", "cancelled"] as FilterStatus[]).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
              filter === f ? "bg-indigo-50 text-indigo-700" : "text-slate-500 hover:bg-slate-50"
            }`}
          >
            {f === "active" ? "Active" : f === "all" ? "All" : f === "needs_resolution" ? "Conflicts" : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="px-5 py-12 text-center text-[13px] text-slate-400">Loading...</div>
      ) : requests.length === 0 ? (
        <div className="px-5 py-12 text-center text-[13px] text-slate-400">
          No {filter === "all" ? "" : filter + " "}rebase requests
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {requests.map(r => {
            const cfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.open;
            const StatusIcon = cfg.icon;
            const isExpanded = expandedId === r.id;
            return (
              <div key={r.id} className="px-5 py-3.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button onClick={() => setExpandedId(isExpanded ? null : r.id)} className="p-0.5">
                      <ChevronDown className={`size-3.5 text-slate-400 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                    </button>
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-medium text-slate-700">{branchName(r.branchId)}</span>
                      <span className="text-slate-400">→</span>
                      <span className="text-[13px] font-medium text-slate-700">{branchName(r.targetBranchId)}</span>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${cfg.bg} ${cfg.text} border ${cfg.border}`}>
                      <StatusIcon className="size-2.5" />
                      {cfg.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-slate-400">
                      {new Date(r.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    </span>
                    {actionLoading === r.id ? (
                      <span className="text-[11px] text-slate-400 animate-pulse">Processing...</span>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        {r.status === "open" && isAdmin && (
                          <button onClick={() => approve(r.id)}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200/60 transition-colors">
                            <Check className="size-3" /> Approve
                          </button>
                        )}
                        {r.status === "approved" && isAdmin && (
                          <button onClick={() => execute(r.id)}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200/60 transition-colors">
                            <Play className="size-3" /> Execute
                          </button>
                        )}
                        {(r.status === "open" || r.status === "approved") && isAdmin && (
                          <button onClick={() => cancel(r.id)}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium text-slate-500 bg-slate-50 hover:bg-slate-100 border border-slate-200/60 transition-colors">
                            <X className="size-3" /> Cancel
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {isExpanded && (
                  <div className="mt-3 ml-8 p-3 rounded-lg bg-slate-50 border border-slate-100 text-[12px] text-slate-600 space-y-1.5">
                    <div className="flex gap-8">
                      <div><span className="text-slate-400">Created by:</span> {r.createdBy}</div>
                      <div><span className="text-slate-400">Created:</span> {new Date(r.createdAt).toLocaleString()}</div>
                    </div>
                    {r.approvedBy && (
                      <div className="flex gap-8">
                        <div><span className="text-slate-400">Approved by:</span> {r.approvedBy}</div>
                        <div><span className="text-slate-400">Approved:</span> {r.approvedAt ? new Date(r.approvedAt).toLocaleString() : "—"}</div>
                      </div>
                    )}
                    {r.executedAt && (
                      <div><span className="text-slate-400">Executed:</span> {new Date(r.executedAt).toLocaleString()}</div>
                    )}
                    {r.status === "needs_resolution" && (
                      <div className="mt-2 p-2 rounded-md bg-red-50 border border-red-100 text-red-700 text-[11px]">
                        This request has merge conflicts that need manual resolution before it can be executed.
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

/* ── Commits Tab ──────────────────────────────────────────────────────── */
function CommitsTab({ projectSlug, branches, commitBranchId, setCommitBranchId, commits, commitLoading, commitTotal, loadingMore, onLoadMore, onRefresh: _onRefresh }: {
  projectSlug: string;
  branches: Branch[];
  commitBranchId: string;
  setCommitBranchId: (id: string) => void;
  commits: Commit[];
  commitLoading: boolean;
  commitTotal: number;
  loadingMore: boolean;
  onLoadMore: () => void;
  onRefresh: () => void;
}) {
  const navigate = useNavigate();

  // Fetch first section slug for peek-mode navigation
  const [firstSectionSlug, setFirstSectionSlug] = useState<string | null>(null);
  useEffect(() => {
    if (!projectSlug) return;
    (async () => {
      const res = await authFetch(`/api/projects/${projectSlug}/studio/sections`);
      if (res.ok) {
        const sections: { slug: string }[] = await res.json();
        if (sections.length > 0) setFirstSectionSlug(sections[0].slug);
      }
    })();
  }, [projectSlug]);

  // Custom branch dropdown state
  const [branchDropOpen, setBranchDropOpen] = useState(false);
  const branchDropRef = useRef<HTMLDivElement>(null);
  const selectedBranch = branches.find(b => b.id === commitBranchId);

  useEffect(() => {
    if (!branchDropOpen) return;
    const handler = (e: MouseEvent) => {
      if (branchDropRef.current && !branchDropRef.current.contains(e.target as Node)) setBranchDropOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [branchDropOpen]);

  // Selected commit for inline diff
  const [selectedDiffCommit, setSelectedDiffCommit] = useState<Commit | null>(null);

  // Auto-select HEAD commit when commits load
  useEffect(() => {
    if (commits.length > 0 && !selectedDiffCommit) setSelectedDiffCommit(commits[0]);
  }, [commits]);

  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 160px)" }}>
      {/* Split pane: commit list left, diff right */}
      <div className="flex-1 min-h-0 flex overflow-hidden">
        {/* Left: branch selector + commit list */}
        <div className="w-[300px] shrink-0 border-r border-slate-200 bg-slate-50/30 flex flex-col overflow-hidden">
          {/* Branch selector — compact, inside left pane */}
          <div className="px-3 py-2 border-b border-slate-200 shrink-0">
            <div ref={branchDropRef} className="relative">
              <button
                onClick={() => setBranchDropOpen(!branchDropOpen)}
                className="w-full flex items-center gap-2 px-2.5 py-1.5 text-[11px] border border-slate-200 rounded-md bg-white hover:border-slate-300 focus:outline-none focus:ring-1 focus:ring-indigo-300 transition-colors"
              >
                <GitBranch className="size-3 text-slate-400" />
                <span className={selectedBranch ? "text-slate-700 font-medium truncate" : "text-slate-400 truncate"}>
                  {selectedBranch ? selectedBranch.name : "Select branch..."}
                </span>
                {selectedBranch?.name === "main" && (
                  <span className="px-1 py-px rounded text-[8px] font-medium bg-emerald-50 text-emerald-600 border border-emerald-200/60 shrink-0">live</span>
                )}
                <ChevronDown className={`size-3 text-slate-400 ml-auto shrink-0 transition-transform ${branchDropOpen ? "rotate-180" : ""}`} />
              </button>
              {branchDropOpen && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 py-1 max-h-60 overflow-y-auto">
                  {branches.map(b => (
                    <button
                      key={b.id}
                      onClick={() => { setCommitBranchId(b.id); setBranchDropOpen(false); setSelectedDiffCommit(null); }}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-[11px] transition-colors ${
                        commitBranchId === b.id ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      <GitBranch className={`size-3 shrink-0 ${commitBranchId === b.id ? "text-indigo-500" : "text-slate-400"}`} />
                      <span className="font-medium truncate">{b.name}</span>
                      {b.name === "main" && (
                        <span className="ml-auto px-1 py-px rounded text-[8px] font-medium bg-emerald-50 text-emerald-600 border border-emerald-200/60 shrink-0">live</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Commit list */}
          {commitLoading ? (
            <div className="flex-1 flex items-center justify-center text-[12px] text-slate-400">Loading...</div>
          ) : !commitBranchId ? (
            <div className="flex-1 flex items-center justify-center text-[12px] text-slate-400">Select a branch</div>
          ) : commits.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-[12px] text-slate-400">No commits</div>
          ) : (
            <div className="flex-1 overflow-y-auto scrollbar-mac">
              {commits.map((c, i) => {
                const isSelected = selectedDiffCommit?.id === c.id;
                const changes = c.changes || [];
                const msg = c.message || changes.map(ch => `${ch.action} ${ch.filePath}`).join(", ") || "(no message)";
                const addedCount = changes.filter(ch => ch.action === "create").length;
                const modifiedCount = changes.filter(ch => ch.action === "update").length;
                const deletedCount = changes.filter(ch => ch.action === "delete").length;
                return (
                  <div
                    key={c.id}
                    onClick={() => setSelectedDiffCommit(c)}
                    className={`px-3 py-2.5 border-b border-slate-100 cursor-pointer transition-colors ${isSelected ? "bg-indigo-50 border-l-2 border-l-indigo-500" : "hover:bg-slate-50 border-l-2 border-l-transparent"}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`size-[7px] rounded-full shrink-0 ${isSelected ? "bg-indigo-500" : "bg-slate-300"}`} />
                      <span className="text-[11px] font-medium text-slate-800 truncate flex-1">{msg}</span>
                      {i === 0 && <span className="text-[7px] font-bold uppercase px-1 py-px rounded-full bg-indigo-100 text-indigo-600 leading-none shrink-0">HEAD</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-1 ml-[15px]">
                      {c.authorName && <span className="text-[9px] text-slate-500">{c.authorName}</span>}
                      {c.createdAt && <span className="text-[9px] text-slate-400">{new Date(c.createdAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>}
                      {changes.length > 0 && (
                        <span className="flex items-center gap-0.5 ml-auto">
                          {addedCount > 0 && <span className="px-1 py-px rounded text-[8px] font-semibold bg-emerald-50 text-emerald-600">+{addedCount}</span>}
                          {modifiedCount > 0 && <span className="px-1 py-px rounded text-[8px] font-semibold bg-blue-50 text-blue-600">~{modifiedCount}</span>}
                          {deletedCount > 0 && <span className="px-1 py-px rounded text-[8px] font-semibold bg-red-50 text-red-600">-{deletedCount}</span>}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              {commits.length < commitTotal && (
                <button
                  onClick={onLoadMore}
                  disabled={loadingMore}
                  className="w-full py-2 text-[11px] font-medium text-indigo-600 hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  {loadingMore ? "Loading..." : `Load more (${commitTotal - commits.length} older)`}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Right: diff view */}
        <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
          {selectedDiffCommit ? (
            <>
              <div className="px-4 py-2 min-h-[42px] border-b border-indigo-100 bg-indigo-50/30 flex items-center gap-3 shrink-0">
                <GitCommit className="size-3.5 text-indigo-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-medium text-slate-800 truncate">{selectedDiffCommit.message || "(no message)"}</div>
                  <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-500">
                    {selectedDiffCommit.authorName && <span className="font-medium">{selectedDiffCommit.authorName}</span>}
                    {selectedDiffCommit.authorName && selectedDiffCommit.createdAt && <span className="text-slate-300">·</span>}
                    {selectedDiffCommit.createdAt && (
                      <span className="text-slate-400">{new Date(selectedDiffCommit.createdAt).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                    )}
                    <span className="text-slate-300">·</span>
                    <span className="font-mono text-slate-400">{selectedDiffCommit.id.slice(0, 10)}</span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (!firstSectionSlug) return;
                    navigate(`/studio/projects/${projectSlug}/${firstSectionSlug}?peekCommit=${selectedDiffCommit.id}&peekMsg=${encodeURIComponent(selectedDiffCommit.message || "")}&peekSeq=${selectedDiffCommit.sequence}`);
                  }}
                  disabled={!firstSectionSlug}
                  className="shrink-0 px-2.5 py-1 rounded-md text-[10px] font-medium text-slate-600 bg-slate-50 border border-slate-200 hover:bg-slate-100 transition-colors disabled:opacity-40"
                >
                  View
                </button>
              </div>
              <CommitDiffPanel
                commitA={selectedDiffCommit.parentId || null}
                commitB={selectedDiffCommit.id}
                projectSlug={projectSlug}
                branchId={commitBranchId}
                beforeLabel="Before"
                afterLabel={selectedDiffCommit.id.slice(0, 7)}
              />
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-[12px] text-slate-400">
              Select a commit to view changes
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Builds Tab ───────────────────────────────────────────────────────── */
function BuildsTab({ builds, buildTotal, buildLoading, onRefresh, onLoadMore, projectId, isAdmin }: {
  builds: Build[];
  buildTotal: number;
  buildLoading: boolean;
  onRefresh: () => void;
  onLoadMore: () => void;
  projectId: string;
  isAdmin: boolean;
}) {
  const [triggering, setTriggering] = useState(false);
  const hasInProgress = builds.some(b => b.status === "building");

  const triggerBuild = async () => {
    if (!projectId || triggering || hasInProgress) return;
    setTriggering(true);
    try {
      const res = await authFetch(`/api/admin/projects/${projectId}/build`, { method: "POST" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || "Build failed");
      }
    } finally {
      setTriggering(false);
      onRefresh();
    }
  };

  // Only blank the tab on the first load, so "Show more" and SSE refreshes keep the list and scroll.
  if (buildLoading && builds.length === 0) {
    return <div className="px-5 py-12 text-center text-[13px] text-slate-400">Loading builds...</div>;
  }
  if (builds.length === 0) {
    return (
      <div className="px-5 py-12 text-center">
        <Package className="size-8 text-slate-300 mx-auto mb-2" />
        <p className="text-[13px] text-slate-400">No builds yet</p>
        <p className="text-[11px] text-slate-400 mt-1">Builds are created automatically when content is merged to main</p>
        {isAdmin && (
          <button onClick={triggerBuild} disabled={triggering}
            className="mt-3 px-3 py-1.5 text-[12px] font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-1.5">
            {triggering ? <Loader2 className="size-3 animate-spin" /> : <Play className="size-3" />}
            {triggering ? "Building..." : "Build"}
          </button>
        )}
      </div>
    );
  }
  return (
    <>
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <span className="text-[12px] text-slate-500">{buildTotal} total build{buildTotal !== 1 ? "s" : ""}</span>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button onClick={triggerBuild} disabled={triggering || hasInProgress}
              className="text-[11px] font-medium bg-indigo-600 text-white px-2.5 py-1 rounded-md hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-1">
              {triggering ? <Loader2 className="size-3 animate-spin" /> : <Play className="size-3" />}
              {triggering ? "Building..." : hasInProgress ? "Build in progress" : "Build"}
            </button>
          )}
          <button onClick={onRefresh} className="text-[11px] text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
            <RefreshCw className="size-3" /> Refresh
          </button>
        </div>
      </div>
      <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto scrollbar-mac">
        {builds.map(b => {
          const st = BUILD_STATUS[b.status] || BUILD_STATUS.completed;
          const StIcon = st.icon;
          return (
            <div key={b.id} className="flex items-center gap-4 px-5 py-3">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className="text-[13px] font-semibold text-slate-700 font-mono">#{b.buildNumber}</span>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${st.bg} ${st.color}`}>
                  <StIcon className={`size-2.5 ${b.status === "building" ? "animate-spin" : ""}`} />
                  {st.label}
                </span>
              </div>
              <div className="flex items-center gap-4 shrink-0 text-[11px] text-slate-500">
                {b.status === "completed" && (
                  <span className="flex items-center gap-1">
                    <FileText className="size-3 text-slate-400" />
                    {b.pagesStored + b.pagesDeduped} pages
                    {b.pagesDeduped > 0 && <span className="text-slate-400">({b.pagesDeduped} reused)</span>}
                  </span>
                )}
                {b.builtByName && <span>{b.builtByName}</span>}
                <span className="font-mono text-[10px] text-slate-400" title={b.contentHash}>{b.contentHash.slice(0, 8)}</span>
                {b.createdAt && (
                  <span className="text-[10px] text-slate-400">
                    {new Date(b.createdAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </span>
                )}
              </div>
            </div>
          );
        })}
        {builds.length < buildTotal && (
          <button onClick={onLoadMore} disabled={buildLoading}
            className="w-full py-2.5 text-[11px] font-medium text-indigo-600 hover:bg-slate-50 transition-colors disabled:opacity-50">
            {buildLoading ? "Loading..." : `Show more (${buildTotal - builds.length} older)`}
          </button>
        )}
      </div>
    </>
  );
}

/* ── Settings Tab ─────────────────────────────────────────────────────── */
function SettingsTab({ isPublished, isAdmin, publishToggling, togglePublished }: {
  isPublished: boolean;
  isAdmin: boolean;
  publishToggling: boolean;
  togglePublished: () => void;
}) {
  return (
    <div className="px-5 py-5 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Globe className="size-4 text-indigo-500" />
            <span className="text-[13px] font-medium text-slate-700">External Publishing</span>
          </div>
          <p className="text-[11px] text-slate-400 ml-6">
            {/* Was "External users with membership can access it", which
                access_control makes impossible: a membership grants nothing to
                an external user, and a private project denies them outright.
                Publishing controls whether the reader surface is live; who can
                read it is the project's audience, set on the sharing tab. */}
            {isPublished
              ? "This project's reader surface is live at /docs. Who can actually read it is set by its audience on the Sharing tab."
              : "This project is not exposed via /docs. Enable to make the reader surface live."}
          </p>
        </div>
        {isAdmin ? (
          <button
            onClick={togglePublished}
            disabled={publishToggling}
            className={`relative w-11 h-6 rounded-full transition-colors ${isPublished ? "bg-emerald-500" : "bg-slate-200"} ${publishToggling ? "opacity-50" : ""}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${isPublished ? "translate-x-5" : ""}`} />
          </button>
        ) : (
          <span className={`px-2 py-1 rounded text-[11px] font-medium ${isPublished ? "bg-emerald-50 text-emerald-600" : "bg-slate-50 text-slate-500"}`}>
            {isPublished ? "Published" : "Not Published"}
          </span>
        )}
      </div>
    </div>
  );
}

